/**
 * Утилиты для работы с партнёрскими ссылками Trip.com
 * 
 * Этот модуль предоставляет функции для:
 * 1. Проверки, является ли URL ссылкой на Trip.com
 * 2. Добавления партнёрских параметров к ссылкам Trip.com
 */

/**
 * Конфигурация партнёрских параметров для Trip.com
 * @typedef {Object} TripAffiliateConfig
 * @property {string} SID - Partner ID
 * @property {string} allianceid - Alliance ID
 * @property {string} utm_campaign - UTM Campaign ID
 * @property {string} locale - Локаль (ru-RU, en-US и т.д.)
 * @property {string} sub1 - Значение для trip_sub1 (генерируется динамически)
 */

/**
 * Список доменов Trip.com для проверки
 */
const TRIP_HOSTS = new Set([
  'www.trip.com',
  'trip.com',
  'uk.trip.com',
  'us.trip.com',
  'ru.trip.com',
  'cn.trip.com',
  'jp.trip.com',
  'kr.trip.com',
  'sg.trip.com',
  'th.trip.com',
  'id.trip.com',
  'my.trip.com',
  'ph.trip.com',
  'vn.trip.com',
  'in.trip.com',
  'au.trip.com',
  'nz.trip.com',
  'de.trip.com',
  'fr.trip.com',
  'es.trip.com',
  'it.trip.com',
  'nl.trip.com'
])

/**
 * Домены, которые не нужно трогать (Travelpayouts и прочие партнёрские сети)
 */
const SKIP_DOMAINS = new Set([
  'tp.media',
  'travelpayouts.com'
])

/**
 * Проверяет, является ли URL ссылкой на Trip.com
 * 
 * @param {string} href - URL для проверки
 * @returns {boolean} true, если это ссылка на Trip.com
 * 
 * @example
 * isTripUrl('https://www.trip.com/hotels/list') // true
 * isTripUrl('https://google.com') // false
 */
export function isTripUrl(href) {
  // Проверяем на пустые значения
  if (!href || typeof href !== 'string') {
    return false
  }

  try {
    const url = new URL(href)
    return TRIP_HOSTS.has(url.hostname.toLowerCase())
  } catch {
    // Если URL невалидный, возвращаем false
    return false
  }
}

/**
 * Проверяет, нужно ли пропустить обработку ссылки
 * (например, если это tp.media или другая партнёрская сеть)
 * 
 * @param {string} href - URL для проверки
 * @returns {boolean} true, если ссылку нужно пропустить
 */
export function shouldSkipUrl(href) {
  if (!href || typeof href !== 'string') {
    return true
  }

  try {
    const url = new URL(href)
    const hostname = url.hostname.toLowerCase()

    // Проверяем, содержит ли домен один из доменов для пропуска
    for (const skipDomain of SKIP_DOMAINS) {
      if (hostname.includes(skipDomain)) {
        return true
      }
    }

    return false
  } catch {
    // Если URL невалидный, пропускаем его
    return true
  }
}

// Одноразовые трассировочные параметры сессии, с которой ссылку скопировали.
const TRIP_SESSION_PARAMS = ['hoteluniquekey', 'masterhotelid_tracelogid', 'subStamp', 'isRightClick']

/**
 * Добавляет партнёрские параметры к URL Trip.com
 * 
 * Функция:
 * - Меняет домен на правильный для языка (ru.trip.com для русского, www.trip.com для английского)
 * - Добавляет или обновляет SID, allianceid, utm_campaign, trip_sub1
 * - Устанавливает locale в зависимости от языка
 * - Сохраняет остальные параметры (city, hotelId и т.д.); прошедшие checkIn/checkOut и
 *   сессионные параметры скопированной из браузера ссылки выкидывает
 * 
 * @param {string} href - Исходный URL
 * @param {TripAffiliateConfig} config - Конфигурация партнёрских параметров
 * @param {string} language - Код языка ('ru', 'en', и т.д.) - опционально
 * @returns {string} URL с партнёрскими параметрами
 * 
 * @example
 * const config = {
 *   SID: '2209817',
 *   allianceid: '1094387',
 *   utm_campaign: '633678',
 *   locale: 'ru-RU',
 *   sub1: 'france-post'
 * }
 * 
 * const url = 'https://www.trip.com/hotels/list?city=123'
 * const result = decorateTripUrl(url, config, 'ru')
 * // result: https://ru.trip.com/hotels/list?city=123&SID=2209817&allianceid=1094387...
 */
export function decorateTripUrl(href, config, language = null) {
  // Если URL пустой или невалидный, возвращаем как есть
  if (!href || typeof href !== 'string') {
    return href
  }

  // Если ссылку нужно пропустить, возвращаем как есть
  if (shouldSkipUrl(href)) {
    return href
  }

  // Если это не Trip.com, возвращаем как есть
  if (!isTripUrl(href)) {
    return href
  }

  try {
    const url = new URL(href)

    // Если передан язык, меняем домен на правильный для этого языка
    if (language) {
      const correctDomain = getTripDomainForLanguage(language)
      url.hostname = correctDomain
    }

    // Ссылки в статьях копируют из браузера вместе с датами поиска, и даты протухают:
    // в top-luxury-hotels-in-qatar 9 из 10 ссылок каждого языка вели на checkIn=2025-12-16
    // (аудит 2026-09-16). Прошедшие даты убираем — trip.com подставит ближайшие сам.
    const today = new Date().toISOString().slice(0, 10)
    const checkIn = url.searchParams.get('checkIn') || url.searchParams.get('checkin')
    if (checkIn && /^\d{4}-\d{2}-\d{2}$/.test(checkIn) && checkIn < today) {
      for (const key of ['checkIn', 'checkin', 'checkOut', 'checkout']) url.searchParams.delete(key)
    }
    for (const key of TRIP_SESSION_PARAMS) url.searchParams.delete(key)

    // Устанавливаем или обновляем партнёрские параметры
    // Перезаписываем, чтобы не потерять атрибуцию
    url.searchParams.set('SID', config.SID)
    url.searchParams.set('allianceid', config.allianceid)
    url.searchParams.set('utm_campaign', config.utm_campaign)
    url.searchParams.set('trip_sub1', config.sub1)

    // Устанавливаем locale, если он указан
    if (config.locale) {
      url.searchParams.set('locale', config.locale)
    }

    return url.toString()
  } catch (error) {
    // Если произошла ошибка при обработке, возвращаем исходный URL
    console.warn('Ошибка при обработке Trip.com URL:', error)
    return href
  }
}

/**
 * Генерирует значение для trip_sub1 на основе данных поста и страны
 * 
 * Логика:
 * - Если статья глобальная (isGlobal=true): используем slug поста
 * - Если привязана к стране: используем <countrySlug>-post
 * - Можно расширить: <countrySlug>-<postSlug>
 * 
 * @param {Object} post - Объект поста из Sanity
 * @param {boolean} post.isGlobal - Глобальная ли статья
 * @param {Array} post.countries - Массив стран (если есть)
 * @param {Object} post.slug - Объект slug поста
 * @param {string} post.slug.current - Текущий slug
 * @returns {string} Значение для trip_sub1
 * 
 * @example
 * const post = { isGlobal: true, slug: { current: 'best-hotels-europe' } }
 * generateTripSub1(post) // 'best-hotels-europe'
 * 
 * const post2 = { 
 *   isGlobal: false, 
 *   countries: [{ slug: 'france' }],
 *   slug: { current: 'paris-guide' }
 * }
 * generateTripSub1(post2) // 'france-post'
 */
export function generateTripSub1(post) {
  if (!post) {
    return 'post'
  }

  // Если статья глобальная, используем slug поста
  if (post.isGlobal) {
    return post.slug?.current || 'global-post'
  }

  // Если привязана к стране, берём первую страну
  const firstCountry = post.countries?.[0]
  if (firstCountry?.slug) {
    // Можно использовать просто slug страны с суффиксом
    return `${firstCountry.slug}-post`

    // Или комбинацию страна-slug поста (раскомментируйте при необходимости):
    // const postSlug = post.slug?.current || 'article'
    // return `${firstCountry.slug}-${postSlug}`
  }

  // Fallback: используем slug поста
  return post.slug?.current || 'post'
}

/**
 * Конвертирует код языка в формат locale для Trip.com
 * 
 * @param {string} language - Код языка ('ru', 'en', и т.д.)
 * @returns {string} Locale в формате xx-XX
 * 
 * @example
 * getLocaleFromLanguage('ru') // 'ru-RU'
 * getLocaleFromLanguage('en') // 'en-US'
 */
export function getLocaleFromLanguage(language) {
  const localeMap = {
    'ru': 'ru-RU',
    'en': 'en-US',
    'es': 'es-ES',
    'de': 'de-DE',
    'fr': 'fr-FR',
    'it': 'it-IT',
    'pt': 'pt-PT',
    'ja': 'ja-JP',
    'ko': 'ko-KR',
    'zh': 'zh-CN'
  }

  return localeMap[language] || 'en-US'
}

/**
 * Получает правильный домен Trip.com для указанного языка
 * 
 * @param {string} language - Код языка ('ru', 'en', и т.д.)
 * @returns {string} Домен Trip.com для этого языка
 * 
 * @example
 * getTripDomainForLanguage('ru') // 'ru.trip.com'
 * getTripDomainForLanguage('en') // 'www.trip.com'
 */
export function getTripDomainForLanguage(language) {
  const domainMap = {
    'ru': 'ru.trip.com',
    'en': 'www.trip.com',
    'es': 'es.trip.com',
    'de': 'de.trip.com',
    'fr': 'fr.trip.com',
    'it': 'it.trip.com',
    'pt': 'www.trip.com',
    'ja': 'jp.trip.com',
    'ko': 'kr.trip.com',
    'zh': 'cn.trip.com'
  }

  return domainMap[language] || 'www.trip.com'
}

/**
 * Партнёрские данные по умолчанию для Trip.com
 * Можно использовать как базовую конфигурацию
 */
export const DEFAULT_TRIP_AFFILIATE_PARAMS = {
  SID: '2209817',
  allianceid: '1094387',
  utm_campaign: '633678'
}

